import type { AdConversionEventOption } from './ad-conversions/ad-conversion-event-options';

/**
 * The Ad Conversions a Form or a Conversion Page can report to the OpenAI
 * Pixel: OpenAI's standard events (developers.openai.com/ads/supported-events)
 * that a form submission, a booking or a page visit can stand for. Standard
 * events are what its conversion-optimised bidding learns from, so no custom
 * events are offered. Left out: `page_viewed`, already sent on every page load
 * (it would count a Conversion Page twice), `subscription_created` (a paid
 * subscription), the shop events, and `app_installed` / `app_opened`, which
 * the Pixel does not support.
 *
 * `dataType` is the OpenAI data shape each event is measured with, which
 * `trackOpenAiEvent` sends as `type`.
 * Forms and Conversion Pages offer them as their "OpenAI Event" options
 * (`adConversionFields`), and `trackOpenAiEvent` sends them.
 */
export const OPENAI_AD_CONVERSION_EVENTS = [
  {
    label: 'Lead created',
    value: 'lead_created',
    dataType: 'customer_action',
    examples: ['a callback request', 'a wait list sign-up', 'a newsletter sign-up'],
  },
  {
    label: 'Appointment scheduled',
    value: 'appointment_scheduled',
    dataType: 'customer_action',
    examples: ['a booked haircut or meeting'],
  },
  { label: 'Registration completed', value: 'registration_completed', dataType: 'customer_action' },
  { label: 'Trial started', value: 'trial_started', dataType: 'plan_enrollment' },
] as const satisfies readonly (AdConversionEventOption & { dataType: string })[];

export type OpenAiAdConversionEvent = (typeof OPENAI_AD_CONVERSION_EVENTS)[number]['value'];

export type OpenAiAdConversionDataType = (typeof OPENAI_AD_CONVERSION_EVENTS)[number]['dataType'];
